import { customElement } from 'lit/decorators.js';
import { html } from 'lit';
import {
  ModalPickerDialog,
  type ModalPickerDialogOptions
} from '../../../../../webmodule-common/other/ui/modal-picker-base';
import { DataTableWrapper } from '../../../../../webmodule-common/other/ui/datatable-view';
import type { RequestPage, ResultPaginated } from '../../../../../webmodule-common/other/ui/RequestPage';
import type {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../../webmodule-common/components/src/webmodule-components';
import type { NullPromise } from '../../../../../webmodule-common/other/ui/events';
import { v6Util, type ExtrusionLengthOption } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { LangFabricationPurchaseOrder } from '../../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { fireQuickErrorToast } from '../../../../../webmodule-common/other/toast-away';

interface LengthStepTableOptions {
  supplierId: string;
  extrusionRef: string;
  finishColorRef: string;
  // Items already fetched by the caller (so the picker does not re-fetch); falls back to a lazy fetch.
  preloaded?: ExtrusionLengthOption[];
  onPick?: (l: ExtrusionLengthOption) => void;
}

@customElement('wm-postep-lengthtable')
class LengthStepTable extends DataTableWrapper<ExtrusionLengthOption> {
  private opts: LengthStepTableOptions;
  private _items: ExtrusionLengthOption[] | null;

  constructor(opts: LengthStepTableOptions) {
    super();
    this.opts = opts;
    this._items = opts.preloaded ?? null;
    this.clickrows = true;
  }

  override disablePagination(): boolean {
    return true;
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  override async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<ExtrusionLengthOption>> {
    this._items ??= await v6Util().getExtrusionLengths(
      this.opts.supplierId,
      this.opts.extrusionRef,
      this.opts.finishColorRef
    );
    const items = this._items;
    return { count: items.length, pageCount: 1, pageIndex: 0, pageSize: items.length, results: items };
  }

  // No column is `sortable`: getRowsFromServer ignores the request and returns the list in
  // the order V6 supplied it, and the table does not sort client-side - so a sortable header
  // would re-fetch and return the identical order.
  override getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFabricationPurchaseOrder.pickerLengthColumn(),
        fieldName: 'display',
        fade: true,
        displayValue: (_t: WebModuleTable, item: unknown) => html`${(item as ExtrusionLengthOption).display}`
      },
      {
        title: () => LangFabricationPurchaseOrder.pickerCategoryColumn(),
        fieldName: 'category',
        width: '120px',
        displayValue: (_t: WebModuleTable, item: unknown) => {
          const l = item as ExtrusionLengthOption;
          const isSpecial = l.category === 'special';
          const label = isSpecial
            ? LangFabricationPurchaseOrder.lengthSpecial()
            : LangFabricationPurchaseOrder.lengthStandard();
          return html`<webmodule-tag size="small" variant=${isSpecial ? 'warning' : 'neutral'}
            >${label}</webmodule-tag
          >`;
        }
      }
    ];
  }

  override async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: ExtrusionLengthOption }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.eventHandler(async () => {
      this.opts.onPick?.(e.detail.item);
    });
  }
}

interface LengthStepPickerOptions extends ModalPickerDialogOptions<ExtrusionLengthOption> {
  supplierId: string;
  extrusionRef: string;
  finishColorRef: string;
  preloaded?: ExtrusionLengthOption[];
}

class LengthStepPicker extends ModalPickerDialog<ExtrusionLengthOption> {
  private table: LengthStepTable;

  constructor(options: LengthStepPickerOptions) {
    super(options);
    this.table = new LengthStepTable({
      supplierId: options.supplierId,
      extrusionRef: options.extrusionRef,
      finishColorRef: options.finishColorRef,
      preloaded: options.preloaded,
      onPick: l => this.select(l)
    });
  }

  protected get modalSize(): string {
    return 'modal-xl';
  }

  bodyTemplate() {
    return html`${this.table}`;
  }

  footerTemplate() {
    return html`<webmodule-button size="small" variant="default" @click=${() => this.cancel()}>
      ${LangFabricationPurchaseOrder.pickerCancel()}
    </webmodule-button>`;
  }
}

export async function pickExtrusionLength(
  supplierId: string,
  extrusionRef: string,
  finishColorRef: string,
  title: string
): NullPromise<ExtrusionLengthOption> {
  const all = await v6Util().getExtrusionLengths(supplierId, extrusionRef, finishColorRef);
  // Nothing available - toast and bail rather than open an empty modal the user can only cancel.
  if (all.length === 0) {
    fireQuickErrorToast(LangFabricationPurchaseOrder.noLengthsAvailable());
    return null;
  }
  // Nothing to choose when there is a single length - auto-select it and skip the picker.
  if (all.length === 1) return all[0];
  return new Promise<ExtrusionLengthOption | null>(resolve => {
    const picker = new LengthStepPicker({
      onSelect: l => resolve(l),
      onCancel: () => resolve(null),
      title,
      supplierId,
      extrusionRef,
      finishColorRef,
      preloaded: all
    });
    picker.showModal().then(() => resolve(null));
  });
}
